import {
  useTaxConfigurationsListQuery,
  useTaxConfigurationUpdateMutation,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import {
  getSelectedTaxStrategy,
  getTaxAppId,
  getTaxCalculationStrategy,
} from "@dashboard/taxes/pages/TaxChannelsPage/helpers";
import { useTaxStrategyChoices } from "@dashboard/taxes/pages/TaxChannelsPage/useTaxStrategyChoices";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Text } from "@saleor/macaw-ui-next";
import { Info } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";

import { StepWrapper } from "../../components/StepWrapper";
import { useWizardContext } from "../../context/WizardContext";
import { messages } from "./messages";
import { taxesSchema } from "./schema";
import { TAXES_FORM_ID, TaxesForm } from "./TaxesForm";
import { TaxesFormValues } from "./types";

export const TaxesStep: React.FC = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const { completeCurrentStep, goToNextStep } = useWizardContext();

  const { data: taxConfigData } = useTaxConfigurationsListQuery({
    variables: { first: 1 },
  });

  const taxConfigurations = mapEdgesToItems(taxConfigData?.taxConfigurations);
  const taxConfig = taxConfigurations?.[0];

  const { taxStrategyChoices: rawStrategyChoices, loading: strategyLoading } =
    useTaxStrategyChoices();

  const taxStrategyChoices = rawStrategyChoices.map(({ endAdornment, ...choice }) => choice);

  const [updateTaxConfig, { loading }] = useTaxConfigurationUpdateMutation({
    onCompleted: data => {
      const errors = data?.taxConfigurationUpdate?.errors ?? [];

      if (errors.length === 0) {
        notify({ status: "success", text: intl.formatMessage(messages.successMessage) });
        completeCurrentStep();
        goToNextStep();
      } else {
        notify({
          status: "error",
          text: errors[0]?.message ?? intl.formatMessage(messages.errorMessage),
        });
      }
    },
  });

  const formValues: TaxesFormValues = {
    chargeTaxes: taxConfig?.chargeTaxes ?? false,
    taxCalculationStrategy: taxConfig ? getSelectedTaxStrategy(taxConfig) : "",
    displayGrossPrices: taxConfig?.displayGrossPrices ?? false,
    pricesEnteredWithTax: taxConfig?.pricesEnteredWithTax ?? false,
  };

  const { control, handleSubmit } = useForm<TaxesFormValues>({
    resolver: zodResolver(taxesSchema),
    defaultValues: formValues,
    values: taxConfig ? formValues : undefined,
  });

  const onSubmit = async (data: TaxesFormValues) => {
    if (!taxConfig) {
      notify({
        status: "warning",
        text: intl.formatMessage(messages.warningMessage),
      });
      completeCurrentStep();
      goToNextStep();

      return;
    }

    await updateTaxConfig({
      variables: {
        id: taxConfig.id,
        input: {
          chargeTaxes: data.chargeTaxes,
          taxCalculationStrategy: data.chargeTaxes
            ? getTaxCalculationStrategy(data.taxCalculationStrategy)
            : null,
          taxAppId: getTaxAppId(data.taxCalculationStrategy),
          displayGrossPrices: data.displayGrossPrices,
          pricesEnteredWithTax: data.pricesEnteredWithTax,
        },
      },
    });
  };

  if (!taxConfig && taxConfigData) {
    return (
      <StepWrapper
        title={intl.formatMessage(messages.title)}
        description={intl.formatMessage(messages.noConfigDescription)}
        skippable={true}
        formId={TAXES_FORM_ID}
        isSaving={false}
      >
        <Box
          display="flex"
          gap={3}
          padding={4}
          borderWidth={1}
          borderStyle="solid"
          borderColor="default1"
          borderRadius={4}
          __backgroundColor="rgba(148, 163, 184, 0.04)"
        >
          <Box __flexShrink={0} marginTop={0.5}>
            <Info size={18} />
          </Box>
          <Text color="default2" size={3}>
            {intl.formatMessage(messages.noConfigFallback)}
          </Text>
        </Box>
        <form
          id={TAXES_FORM_ID}
          onSubmit={e => {
            e.preventDefault();
            completeCurrentStep();
            goToNextStep();
          }}
        />
      </StepWrapper>
    );
  }

  return (
    <StepWrapper
      title={intl.formatMessage(messages.title)}
      description={intl.formatMessage(messages.description)}
      skippable={true}
      formId={TAXES_FORM_ID}
      isSaving={loading}
    >
      <TaxesForm
        control={control}
        disabled={loading}
        onSubmit={handleSubmit(onSubmit)}
        strategyChoices={taxStrategyChoices}
        strategyChoicesLoading={strategyLoading}
      />
    </StepWrapper>
  );
};
